import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import API_BASE from "./apiConfig";
import { Colors } from "./branding";
import { useTheme, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";

const C = { get yellow() { return getBrandColors().yellow; }, get blue() { return getBrandColors().blue; } };

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const HOURS = [6,7,8,9,10,11,12,13,14,15,16,17,18,19,20];
const fmt12h = h => h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h-12} PM`;

// dow: 0=Mon … 6=Sun (matches DB WEEKDAY())
const DEFAULT_KEYS = new Set([
  "0-16","0-17","0-20",
  "1-16","1-17","1-20",
  "2-16","2-17","2-20",
  "3-16","3-17","3-20",
  "4-16","4-17","4-20",
  "5-9","5-10",
]);

const cellKey = (dow, hour) => `${dow}-${hour}`;

const slotsToGrid = slots => {
  const g = new Set();
  (slots || []).forEach(s => {
    const startH = parseInt((s.start_time || "").split(":")[0], 10);
    const endH   = parseInt((s.end_time   || "").split(":")[0], 10);
    if (!isNaN(startH) && !isNaN(endH) && endH > startH) {
      for (let h = startH; h < endH; h++) g.add(cellKey(s.day_of_week, h));
    } else if (!isNaN(startH)) {
      g.add(cellKey(s.day_of_week, startH));
    }
  });
  return g;
};

const gridToSlots = grid => {
  const out = [];
  grid.forEach(k => {
    const [d, h] = k.split("-").map(Number);
    out.push({ day_of_week: d, start_time: `${String(h).padStart(2,"0")}:00`, end_time: `${String(h+1).padStart(2,"0")}:00` });
  });
  return out;
};

// Build weeks for a month: rows of 7 items, null = padding
function buildCalendar(year, month) {
  const first   = new Date(year, month - 1, 1);
  const last    = new Date(year, month, 0);
  const startDow = (first.getDay() + 6) % 7; // 0=Mon
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push({ date: d, dow: (new Date(year, month-1, d).getDay() + 6) % 7 });
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i+7));
  return weeks;
}

// ── Hour editor panel ─────────────────────────────────────────────────────────
function HourPanel({ dow, dayLabel, grid, booked, onChange, isDark }) {
  const T = isDark;
  const textMuted = T ? "rgba(255,255,255,0.45)" : "rgba(3,78,123,0.50)";
  const toggle = (hour) => {
    const k = cellKey(dow, hour);
    if (booked.has(k)) return;
    const next = new Set(grid);
    next.has(k) ? next.delete(k) : next.add(k);
    onChange(next);
  };
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, color: T ? "#fff" : C.blue, marginBottom: 10 }}>
        {dayLabel} — Time Slots
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {HOURS.map(h => {
          const k = cellKey(dow, h);
          const isBooked = booked.has(k);
          const isOn     = grid.has(k);
          let bg, color, cursor;
          if (isBooked)      { bg = "rgba(220,38,38,0.15)"; color = getBrandColors().red; cursor = "not-allowed"; }
          else if (isOn)     { bg = dow < 5 ? C.blue : C.yellow; color = dow < 5 ? "#fff" : "#1a1200"; cursor = "pointer"; }
          else               { bg = T ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.05)"; color = textMuted; cursor = "pointer"; }
          return (
            <button key={h} onClick={() => toggle(h)} disabled={isBooked}
              title={isBooked ? "Booked — cannot change" : (isOn ? `Remove ${fmt12h(h)} slot` : `Add ${fmt12h(h)} slot`)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "7px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, width: "100%",
                border: isBooked ? "1px solid rgba(220,38,38,0.35)" : "none",
                background: bg, color, cursor }}>
              <span>{fmt12h(h)} – {fmt12h(h+1)}</span>
              <span style={{ fontSize: 14 }}>{isBooked ? "🔴" : isOn ? "✓" : "—"}</span>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: textMuted, marginTop: 10, lineHeight: 1.6 }}>
        Changes apply to <strong>all {dayLabel}s</strong> (recurring weekly).
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function OTOAvailability({ currentUser }) {
  const { isDark: T } = useTheme();
  const isAdmin = currentUser?.user_role === "Admin" || currentUser?.is_superadmin;

  const textPrimary = T ? "rgba(255,255,255,0.90)" : "#1a2a3a";
  const textMuted   = T ? "rgba(255,255,255,0.45)" : "rgba(3,78,123,0.50)";
  const borderColor = T ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.12)";
  const cardBg      = T ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.95)";
  const gc = { background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 16, backdropFilter: "blur(12px)" , WebkitBackdropFilter: "blur(12px)"};

  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [selDow,   setSelDow]   = useState(null);
  const [selLabel, setSelLabel] = useState("");

  const [coaches,  setCoaches]  = useState([]);
  const [selCoach, setSelCoach] = useState(null);
  const [grid,     setGrid]     = useState(new Set(DEFAULT_KEYS));
  const [booked,   setBooked]   = useState(new Set());
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState(null);
  const [changed,  setChanged]  = useState(false);
  const panelRef = useRef(null);

  const loadAvail = useCallback((cid) => {
    setSelCoach(cid);
    setGrid(new Set(DEFAULT_KEYS));
    setBooked(new Set());
    setChanged(false);
    setMsg(null);
    fetch(`${API_BASE}/oto/coaches/${cid}/availability`)
      .then(r => r.json())
      .then(d => {
        if (d.availability && d.availability.length > 0) setGrid(slotsToGrid(d.availability));
        const bk = new Set();
        (d.booked_slots || []).forEach(({ day_of_week, hour }) => bk.add(cellKey(day_of_week, hour)));
        setBooked(bk);
        setChanged(false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetch(`${API_BASE}/oto/coaches`).then(r => r.json()).then(d => {
        setCoaches(d.coaches || []);
        if (d.coaches?.length) loadAvail(d.coaches[0].user_id);
      });
    } else {
      loadAvail(currentUser.user_id);
    }
  }, []); // eslint-disable-line

  const handleGridChange = (nextGrid) => { setGrid(nextGrid); setChanged(true); setMsg(null); };

  const save = async () => {
    if (!selCoach) return;
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/oto/coaches/${selCoach}/availability`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slots: gridToSlots(grid) }),
      });
      const d = await res.json();
      setMsg({ ok: d.success, text: d.success ? "Availability saved!" : (d.message || "Failed") });
      if (d.success) setChanged(false);
    } catch { setMsg({ ok: false, text: "Network error" }); }
    setSaving(false);
  };

  const prevMonth = () => { if (calMonth === 1) { setCalYear(y=>y-1); setCalMonth(12); } else setCalMonth(m=>m-1); setSelDow(null); };
  const nextMonth = () => { if (calMonth === 12) { setCalYear(y=>y+1); setCalMonth(1); } else setCalMonth(m=>m+1); setSelDow(null); };

  const weeks    = buildCalendar(calYear, calMonth);
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  const dowHours = Array.from({ length: 7 }, (_, d) => HOURS.filter(h => grid.has(cellKey(d, h))).length);

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: textPrimary, padding: "24px 28px", maxWidth: 1000 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <PageHeaderIcon icon={<FaCalendarAlt size={20} color={getBrandColors().blueDark} />} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T ? "#fff" : C.blue }}>My Availability</div>
            <div style={{ fontSize: 13, color: textMuted, marginTop: 3 }}>
              Click any day to edit its time slots. Dots show available (blue/yellow) and booked (red) hours.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setGrid(new Set(DEFAULT_KEYS)); setChanged(true); setMsg(null); }}
            style={{ padding: "7px 14px", borderRadius: 8, border: `1px solid ${borderColor}`, background: "transparent",
              cursor: "pointer", fontSize: 12, fontWeight: 600, color: textMuted }}>
            Reset Defaults
          </button>
          <button onClick={save} disabled={saving || !changed}
            style={{ padding: "8px 20px", borderRadius: 8, border: "none",
              cursor: (saving || !changed) ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700,
              background: changed ? C.yellow : (T ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.12)"),
              color: changed ? "#1a1200" : textMuted }}>
            {saving ? "Saving…" : "Save Availability"}
          </button>
        </div>
      </div>

      {/* Coach selector */}
      {isAdmin && coaches.length > 0 && (
        <div style={{ ...gc, padding: "12px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Coach</span>
          {coaches.map(c => (
            <button key={c.user_id} onClick={() => { loadAvail(c.user_id); setSelDow(null); }}
              style={{ padding: "6px 14px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                background: selCoach === c.user_id ? C.blue : (T ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.08)"),
                color: selCoach === c.user_id ? "#fff" : textPrimary }}>
              {c.full_name}
            </button>
          ))}
        </div>
      )}

      {/* Weekly summary pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {DAY_LABELS.map((d, i) => (
          <button key={i}
            onClick={() => { setSelDow(selDow === i ? null : i); setSelLabel(d); }}
            style={{ padding: "5px 12px", borderRadius: 12, fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all 0.14s",
              border: selDow === i ? `2px solid ${C.blue}` : `1px solid ${borderColor}`,
              background: selDow === i ? (T ? "rgba(3,78,123,0.35)" : "rgba(3,78,123,0.10)") : "transparent",
              color: dowHours[i] > 0 ? (T ? "#93c5fd" : C.blue) : textMuted }}>
            {d} · {dowHours[i] > 0 ? `${dowHours[i]}h` : "off"}
          </button>
        ))}
        <span style={{ fontSize: 11, color: textMuted, alignSelf: "center", marginLeft: 4 }}>
          — or click a day below
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: selDow !== null ? "1fr 260px" : "1fr", gap: 18, alignItems: "start" }}>

        {/* Calendar card */}
        <div style={gc}>
          {/* Month navigation */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: `1px solid ${borderColor}` }}>
            <button onClick={prevMonth}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: textPrimary, lineHeight: 1, padding: "0 8px" }}>
              ‹
            </button>
            <span style={{ fontSize: 16, fontWeight: 800 }}>{MONTHS[calMonth - 1]} {calYear}</span>
            <button onClick={nextMonth}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: textPrimary, lineHeight: 1, padding: "0 8px" }}>
              ›
            </button>
          </div>

          {/* Day-of-week headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", padding: "12px 14px 4px" }}>
            {DAY_LABELS.map((d, i) => (
              <div key={i} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.06em", color: i === 5 ? C.yellow : i < 5 ? C.blue : textMuted }}>
                {d}
              </div>
            ))}
          </div>

          {/* Week rows */}
          <div style={{ padding: "6px 14px 16px" }}>
            {weeks.map((week, wi) => (
              <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 5, marginBottom: 5 }}>
                {week.map((cell, ci) => {
                  if (!cell) return <div key={ci}/>;
                  const { date, dow } = cell;
                  const dateStr = `${calYear}-${String(calMonth).padStart(2,"0")}-${String(date).padStart(2,"0")}`;
                  const isToday = dateStr === todayStr;
                  const isPast  = dateStr < todayStr;
                  const isSel   = selDow === dow;
                  const onHours = HOURS.filter(h => grid.has(cellKey(dow, h)));
                  const bkHours = HOURS.filter(h => booked.has(cellKey(dow, h)));

                  return (
                    <div key={ci}
                      onClick={() => {
                        if (isPast) return;
                        setSelDow(prev => prev === dow ? null : dow);
                        setSelLabel(DAY_LABELS[dow]);
                      }}
                      style={{
                        borderRadius: 10, padding: "7px 4px 6px", textAlign: "center",
                        cursor: isPast ? "default" : "pointer",
                        border: isSel ? `2px solid ${C.blue}` : `1px solid ${borderColor}`,
                        background: isSel
                          ? (T ? "rgba(3,78,123,0.30)" : "rgba(3,78,123,0.08)")
                          : isToday
                          ? (T ? "rgba(247,206,11,0.14)" : "rgba(247,206,11,0.18)")
                          : "transparent",
                        opacity: isPast ? 0.38 : 1,
                        transition: "all 0.12s",
                        minHeight: 68,
                      }}>
                      {/* Date number */}
                      <div style={{ fontSize: 13, fontWeight: isToday ? 900 : 600,
                        color: isToday ? C.yellow : isSel ? (T ? "#fff" : C.blue) : textPrimary }}>
                        {date}
                      </div>
                      {isToday && (
                        <div style={{ fontSize: 7, fontWeight: 800, color: C.yellow, letterSpacing: "0.04em", marginBottom: 2 }}>TODAY</div>
                      )}

                      {/* Booked dots (red) */}
                      {bkHours.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2, margin: "3px 0 2px" }}>
                          {bkHours.slice(0, 3).map(h => (
                            <div key={h} title={`${fmt12h(h)} – booked`}
                              style={{ width: 5, height: 5, borderRadius: "50%", background: getBrandColors().red }}/>
                          ))}
                          {bkHours.length > 3 && <span style={{ fontSize: 7, color: getBrandColors().red }}>+{bkHours.length-3}</span>}
                        </div>
                      )}

                      {/* Available dots (blue/yellow) */}
                      {onHours.length > 0 ? (
                        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
                          {onHours.slice(0, 4).map(h => (
                            <div key={h} title={fmt12h(h)}
                              style={{ width: 5, height: 5, borderRadius: "50%", background: dow < 5 ? C.blue : C.yellow }}/>
                          ))}
                          {onHours.length > 4 && <span style={{ fontSize: 7, color: dow < 5 ? C.blue : C.yellow }}>+{onHours.length-4}</span>}
                        </div>
                      ) : !isPast && dow !== 6 && (
                        <div style={{ fontSize: 8, color: textMuted, marginTop: 4 }}>off</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: 16, padding: "10px 18px", borderTop: `1px solid ${borderColor}`, flexWrap: "wrap" }}>
            {[
              { color: C.blue,    label: "Available weekday" },
              { color: C.yellow,  label: "Available Saturday" },
              { color: getBrandColors().red, label: "Booked (locked)" },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }}/>
                <span style={{ fontSize: 11, color: textMuted }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hour editor panel */}
        {selDow !== null && (
          <div ref={panelRef} style={{ ...gc, padding: "16px 18px" }}>
            <HourPanel dow={selDow} dayLabel={selLabel} grid={grid} booked={booked} onChange={handleGridChange} isDark={T} />
          </div>
        )}
      </div>

      {/* Status feedback */}
      {msg && (
        <div style={{ marginTop: 14, padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, display: "inline-block",
          color: msg.ok ? "#16a34a" : "#ef4444",
          background: msg.ok ? "rgba(22,163,74,0.10)" : "rgba(239,68,68,0.10)" }}>
          {msg.ok ? "✓ " : "✗ "}{msg.text}
        </div>
      )}
    </div>
  );
}
